/* eslint-disable max-len */
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { KEY_MESSAGES_STATUS } from 'basics/constants/common.constant';
import { BiogenFunction, Channel, KeyMessageType } from 'basics/enums/maya.enums';
import useGetCRMKeyMessagesLazyQuery from 'basics/graphql/queries/getCRMKeyMessagesLazy';
import useGetKeyMessagesLazyQuery from 'basics/graphql/queries/getKeyMessagesLazy';
import { KeyMessagesOptionType } from 'basics/types/keyMessages.types';
import { CepStateType, TacticStateType } from 'basics/types/maya.types';
import { warningToast } from 'basics/utils/toast';
import { CustomerInitiative, KeyMessage } from 'generated/maya.types';
import cepState from 'states/cep.states';
import tacticState from 'states/tactic.states';

type FetchTacticKeyMessagesOptionsParams = {
  country?: string;
  product?: string;
  executingTeam?: string;
  selectedKeyMessage?: {
    id?: string | number | null;
    name?: string | null;
  } | null;
  includeSelectedKeyMessage?: boolean;
};

const isKeyMessageMandatory = (currentChannel: Channel) => {
  return ([
    Channel.f2fMeeting,
    Channel.oneToOne,
    Channel.oneToOneKeyMessage,
    Channel.telephone,
    Channel.virtualMeeting,
  ].includes(currentChannel));
};

export const useFetchTacticKeyMessagesOptions = () => {
  const flags = useFlags();
  const isInternalKeyMessages = Boolean(
    flags.mayaAkma1445EnableInternalKeyMessages ?? flags['maya-akma-1445-enable-internal-key-messages'],
  );

  const {
    getKeyMessagesLazy: fetchKeyMessages,
  } = useGetKeyMessagesLazyQuery();
  const {
    getKeyMessagesLazy: fetchMedicalStrategies,
  } = useGetKeyMessagesLazyQuery();
  const {
    getKeyMessagesLazy: fetchInternationalMedicalStrategies,
  } = useGetKeyMessagesLazyQuery();
  const {
    getKeyMessagesLazy: fetchDiscussionTopics,
  } = useGetKeyMessagesLazyQuery();

  const {
    getCRMKeyMessagesLazy: fetchCRMKeyMessages,
  } = useGetCRMKeyMessagesLazyQuery();
  const {
    getCRMKeyMessagesLazy: fetchCRMMedicalStrategies,
  } = useGetCRMKeyMessagesLazyQuery();
  const {
    getCRMKeyMessagesLazy: fetchCRMInternationalMedicalStrategies,
  } = useGetCRMKeyMessagesLazyQuery();
  const {
    getCRMKeyMessagesLazy: fetchCRMDiscussionTopics,
  } = useGetCRMKeyMessagesLazyQuery();

  const delayExecution = (ms: number) => {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const getBiogenFunction = (executionTeam: string | undefined, keyMessageElement: KeyMessagesOptionType) => {
    switch (executionTeam) {
      case BiogenFunction.Commercial:
        return keyMessageElement.commercial;
      case BiogenFunction.Medical:
        return keyMessageElement.medical;
      case BiogenFunction.MarketAccess:
        return keyMessageElement.marketAccess;
      default:
        return false;
    }
  };

  const mapKeyMessagesToOptions = (keyMessages: KeyMessage[]): KeyMessagesOptionType[] => {
    return keyMessages.map((keyMessage) => ({
      id: String(keyMessage.ID),
      label: String(keyMessage.KeyName),
      medical: Boolean(keyMessage.Medical),
      commercial: Boolean(keyMessage.Commercial),
      marketAccess: Boolean(keyMessage.MarketAccess),
    }));
  };

  const fetchTacticKeyMessagesOptions = async ({
    country,
    product,
    executingTeam,
    selectedKeyMessage,
    includeSelectedKeyMessage = false,
  }: FetchTacticKeyMessagesOptionsParams): Promise<KeyMessagesOptionType[]> => {
    if (!country || !product) {
      return [];
    }

    const keyMessagesFilters = {
      Country: country === 'GB' && flags.mayaAkma1327UkTestingMaterials ? 'UK' : country,
      Product: product,
      ActiveStatusFlag: KEY_MESSAGES_STATUS,
    };

    const skipValue = !keyMessagesFilters.Country || !keyMessagesFilters.Product;
    const allKeyMessages: KeyMessage[] = [];

    const queryConfigs = [
      {
        fetch: isInternalKeyMessages ? fetchKeyMessages : fetchCRMKeyMessages,
        filters: { ...keyMessagesFilters, Type: KeyMessageType.keyMessage },
        skip: skipValue,
      },
      {
        fetch: isInternalKeyMessages ? fetchMedicalStrategies : fetchCRMMedicalStrategies,
        filters: { ...keyMessagesFilters, Type: KeyMessageType.medicalStrategy },
        skip: skipValue || !flags.mayaBiogenlinc4369EnableFetchAllKeyMessagesTypes,
      },
      {
        fetch: isInternalKeyMessages ? fetchDiscussionTopics : fetchCRMDiscussionTopics,
        filters: { ...keyMessagesFilters, Type: KeyMessageType.discussionTopic },
        skip: skipValue || !flags.mayaBiogenlinc4369EnableFetchAllKeyMessagesTypes,
      },
      {
        fetch: isInternalKeyMessages ? fetchInternationalMedicalStrategies : fetchCRMInternationalMedicalStrategies,
        filters: { ...keyMessagesFilters, Country: 'INT', Type: KeyMessageType.medicalStrategy },
        skip: skipValue || !flags.mayaBiogenlinc4369EnableFetchAllKeyMessagesTypes || !flags.mayaAkma299EnableIntFilterForKeyMessages,
      },
    ];

    await queryConfigs.reduce(async (prevPromise, { fetch, filters, skip }, index) => {
      await prevPromise;

      if (skip) return Promise.resolve();

      const response = await fetch({ variables: { filters } });
      const keyMessagesList = isInternalKeyMessages
        ? response.data?.getKeyMessages
        : response.data?.getCRMKeyMessages;

      allKeyMessages.push(...(keyMessagesList || []));

      if (index < queryConfigs.length - 1) {
        await delayExecution(200);
      }

      return Promise.resolve();
    }, Promise.resolve());

    let keyMessagesOptions = mapKeyMessagesToOptions(allKeyMessages);

    if (flags.mayaBiogenlinc5479KeyMessagePerExecutingTeam) {
      keyMessagesOptions = keyMessagesOptions.filter((element) => {
        return getBiogenFunction(executingTeam, element);
      });
    }

    if (
      includeSelectedKeyMessage
        && selectedKeyMessage?.id
        && !flags.mayaAkma299EnableIntFilterForKeyMessages
    ) {
      const isCurrentKMInKMList = keyMessagesOptions.find((keyMessage) => {
        return keyMessage.id === String(selectedKeyMessage.id);
      });

      if (!isCurrentKMInKMList) {
        keyMessagesOptions.push({
          id: String(selectedKeyMessage.id),
          label: String(selectedKeyMessage.name || ''),
          medical: executingTeam === BiogenFunction.Medical,
          commercial: executingTeam === BiogenFunction.Commercial,
          marketAccess: executingTeam === BiogenFunction.MarketAccess,
        });
      }
    }

    return keyMessagesOptions;
  };

  return {
    fetchTacticKeyMessagesOptions,
  };
};

const useTacticKeyMessages = (executingTeam: string, selectedChannel: Channel | null) => {
  const flags = useFlags();
  const { t } = useTranslation();

  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const tacticStateValue = useSnapshot(tacticState) as TacticStateType;

  const customerInitiative = cepStateValue.cep?.customerInitiative as CustomerInitiative;
  const product = cepStateValue.cep?.product;
  const [keyMessagesOptions, setKeyMessagesOptions] = useState<KeyMessagesOptionType[]>([]);
  const [isKeyMessagesLoaded, setIsKeyMessagesLoaded] = useState<boolean>(false);
  const { fetchTacticKeyMessagesOptions } = useFetchTacticKeyMessagesOptions();

  useEffect(() => {
    let isMounted = true;

    const fetchKeyMessagesOptions = async () => {
      setIsKeyMessagesLoaded(false);

      const options = await fetchTacticKeyMessagesOptions({
        country: customerInitiative?.country,
        product,
        executingTeam,
        selectedKeyMessage: tacticStateValue.tactic?.keyMessage,
        includeSelectedKeyMessage: tacticStateValue.tactic?.executingTeam === executingTeam,
      });

      if (!isMounted) return;

      if (
        isKeyMessageMandatory(selectedChannel as Channel)
          && !options.length
          && executingTeam
          && customerInitiative?.country
          && product
          && flags.mayaBdcfcsd1196ImplementAlertSystem
      ) {
        warningToast(t('maya_no_key_messages_warning'));
      }

      setKeyMessagesOptions(options);
      setIsKeyMessagesLoaded(true);
    };

    fetchKeyMessagesOptions().catch(() => {
      if (!isMounted) return;

      setKeyMessagesOptions([]);
      setIsKeyMessagesLoaded(true);
    });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerInitiative?.country,
    product,
    executingTeam,
    selectedChannel,
    tacticStateValue.tactic?.keyMessage?.id,
    tacticStateValue.tactic?.executingTeam,
  ]);

  return {
    keyMessagesOptions,
    keyMessagesLoading: !isKeyMessagesLoaded,
  };
};

export default useTacticKeyMessages;
